import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';

import { tap, map, catchError, switchMap, retry } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, Observable, of, throwError } from 'rxjs';
import { Hmo, CrudListResponses, CrudSuccessResponse } from '@app/shared/models';
import swal from 'sweetalert2';
import { GetAPIResponse } from '@app/shared/models/crud-responses';
import { AccountAccessGuardService } from '../helpers/account-access-guard.service';

@Injectable({
	providedIn: 'root'
})
export class HmoService {

	apiUrlV2 = environment.apiUrlV2 + '/masterfile/hmo';
	hmo: Observable<Hmo[]>;
	private _hmo: Hmo[] = [];
	private readonly _record = new BehaviorSubject<GetAPIResponse>({});
	public record$ = this._record.asObservable();

	params: any = {
		limit: 100,
		page: 1,
		total: 0,
		sortField: 'updated_dt',
		sortDirection: 'desc'
	};

	constructor(
		private http: HttpClient,
		private accountAccessGuardService: AccountAccessGuardService

	) {

	}

	setParams(params: any) {
    	this.params = params;
  	}
	refreshList() {
        this._record.next(null);
  		this.getInitRecords(this.params).subscribe();
	}


	getInitRecords(params?: any) {
		return this.http.get(`${this.apiUrlV2}`, {params: params}).pipe(
			map(
				(data: GetAPIResponse) => {
					this._record.next(data);
					return <Hmo[]>data.data;
				}
			),
			tap(
				(data: any) => {
					this.hmo = of(data);
				}
			),
			catchError(error => {
				return throwError(error);
			})
		);

	}

	getAll() {
		if (this.hmo) {
			return this.hmo;
		} else {
			return this.getInitRecords(this.params);
		}
	}

	create(data: any): Observable<Hmo> {
		
		return this.accountAccessGuardService.accountGuardRestriction(() => this.http.post(`${this.apiUrlV2}`, data).pipe(
			tap(
				(res: any) => {
					if (res) {
						let record: any = this._record.getValue();
						if (record && Array.isArray(record.data)) {
							record.data = [res, ...record.data];
							if (record.meta) {
								record.meta.total += 1;
							}
							this._record.next(record);
						} else {
							this.refreshList();
						}

						if (this.hmo) {
							this.hmo = this.hmo.pipe(
								map(existingData => [res, ...existingData])
							);
						}
						return of(<Hmo>res);
					}
				},
				error => {
					swal({
						title: 'Save error!',
						text: error.error.message,
						type: 'warning',
						showCancelButton: false,
						confirmButtonColor: '#3085d6'
					});
					return of(null);
				}
			)
		));

	}

	update(id: number, data: any): Observable<Hmo> {
		
		return this.accountAccessGuardService.accountGuardRestriction(() => this.http.put(`${this.apiUrlV2}/${id}`, data).pipe(
			tap(
				(res: any) => {
					let record: any = this._record.getValue();
					if (!record || !Array.isArray(record.data)) {
						this.refreshList();
						return;
					}
					const idx = record.data.findIndex((row) => {
						return row.id == id;
					})
					if (idx >= 0) {
						record.data.splice(idx, 1, res);
						this._record.next(record);
					} else {
						this.refreshList();
					}
				},
				error => {
					swal({
						title: 'Update error!',
						text: error.error.message,
						type: 'warning',
						showCancelButton: false,
						confirmButtonColor: '#3085d6'
					});
					return of(null);
				}
			)
		));

	}

	delete(id: number): Observable<boolean> {
		
		return this.accountAccessGuardService.accountGuardRestriction(() => this.http.delete(`${this.apiUrlV2}/${id}`).pipe(
			tap(
				(data) => {
					let record: any = this._record.getValue();
					if (!record || !Array.isArray(record.data)) {
						this.refreshList();
						return;
					}
					const idx = record.data.findIndex((row) => {
						return row.id == id;
					})
					if (idx >= 0) {
						record.data.splice(idx, 1);
						if (record.meta) {
							record.meta.total -= 1;
						}
						this._record.next(record);
					} else {
						this.refreshList();
					}
				}
			),
			map(
				() => true
			),
			catchError(error => {
				swal({
					title: 'Delete error!',
					text: error.error.message,
					type: 'warning',
					showCancelButton: false,
					confirmButtonColor: '#3085d6'
				});
				return throwError(error);
			})
		));
	}

}
